/* Umbrella Union — AA Release Player (TES + ASL + YouTube IFrame API)
 * Requires: assets/aa-media-coordinator.js
 * Scope:    section root via [data-uu-release-player-root]
 *
 * Changelog:
 *   R-05 (2026-03-10): YouTube embed extracted to aa-release-youtube.liquid.
 *   R-06 (2026-03-12): Cross-section ASL coordinator rebuild.
 *     - Loads YouTube IFrame API once per page (window._uuYTLoading guard)
 *     - Initialises YT.Player on document-scoped [data-uu-youtube]
 *     - Audio → YouTube: audio 'play' calls window._uuYTPlayer.pauseVideo()
 *     - YouTube → Audio: onStateChange PLAYING pauses [data-uu-audio]
 *     - Graceful no-op when YouTube section absent from page
 *     - TES: section:load re-inits; section:unload nulls window._uuYTPlayer
 *     - No new globals beyond window._uuYTPlayer (+ window._uuYTLoading flag)
 */
(function () {
  'use strict';

  const ROOT = (window.UmbrellaUnion = window.UmbrellaUnion || {});

  const stateByRoot = new WeakMap();

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    return root ? Array.from(root.querySelectorAll(sel)) : [];
  }

  function safeText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }

  function safeAttr(el, name, value) {
    if (!el) return;
    if (value) el.setAttribute(name, value);
    else el.removeAttribute(name);
  }

  // ── R-06: YouTube IFrame API coordinator ──────────────────────────────────
  //
  // Document-scoped: [data-uu-youtube] lives in aa-release-youtube.liquid,
  // a different Shopify section wrapper — must query document, not section root.

  function initYTPlayer() {
    const iframe = document.querySelector('[data-uu-youtube]');
    if (!iframe) return; // graceful: no YouTube section on this page — no-op

    window._uuYTPlayer = new window.YT.Player(iframe, {
      events: {
        onStateChange: function (event) {
          if (event.data === window.YT.PlayerState.PLAYING) {
            // YouTube started playing → pause cross-section HTML5 audio
            const audio = document.querySelector('[data-uu-audio]');
            if (audio && !audio.paused) audio.pause();
          }
        },
      },
    });
  }

  function loadYTAPI() {
    if (window.YT && window.YT.Player) {
      // API already present (e.g. Theme Editor section:load after first boot)
      initYTPlayer();
      return;
    }
    if (window._uuYTLoading) return; // script tag already in flight — wait for callback
    window._uuYTLoading = true;

    // Chain onto any pre-existing onYouTubeIframeAPIReady handler (defensive)
    const _prevYTReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (_prevYTReady) _prevYTReady();
      initYTPlayer();
    };

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  }

  // ── Player section initialization ─────────────────────────────────────────

  function initRoot(root) {
    if (!root || stateByRoot.has(root)) return;

    const sectionId = root.getAttribute('data-section-id');
    const playerId  = root.getAttribute('data-player-id') || sectionId || 'main';

    const audio      = qs(root, 'audio[data-uu-audio]');
    const rows       = qsa(root, '[data-uu-track]');
    const btnPrev    = qs(root, '[data-uu-prev]');
    const btnNext    = qs(root, '[data-uu-next]');
    const btnShuffle = qs(root, '[data-uu-shuffle]');

    const activeArt    = qs(root, '[data-uu-active-art]');
    const activeTitle  = qs(root, '[data-uu-active-title]');
    const activeArtist = qs(root, '[data-uu-active-artist]');

    const st = {
      sectionId,
      playerId,
      audio,
      rows,
      btnPrev,
      btnNext,
      btnShuffle,
      activeArt,
      activeTitle,
      activeArtist,
      currentIndex: 0,
      shuffle: false,
      onRowClick: [],
      onNext: null,
      onPrev: null,
      onShuffle: null,
      onEnded: null,
      onPlay: null,
    };

    stateByRoot.set(root, st);

    if (ROOT.aaMediaCoordinator && sectionId) {
      ROOT.aaMediaCoordinator.registerSection(sectionId, { root });
    }

    function markActive(index, isPlaying) {
      st.rows.forEach((r) => r.classList.remove('is-active', 'is-playing'));
      const row = st.rows[index];
      if (!row) return;
      row.classList.add('is-active');
      if (isPlaying) row.classList.add('is-playing');
    }

    function loadTrack(index, autoPlay) {
      if (!st.audio || !st.rows.length) return;

      let nextIndex = index;
      if (nextIndex < 0) nextIndex = st.rows.length - 1;
      if (nextIndex >= st.rows.length) nextIndex = 0;
      st.currentIndex = nextIndex;

      const row    = st.rows[nextIndex];
      const src    = row.getAttribute('data-src');
      const img    = row.getAttribute('data-img');
      const title  = row.getAttribute('data-title');
      const artist = row.getAttribute('data-artist');

      if (src && st.audio.currentSrc !== src) {
        st.audio.src = src;
        st.audio.load();
      }

      if (img) safeAttr(st.activeArt, 'src', img);
      safeText(st.activeTitle, title);
      safeText(st.activeArtist, artist);

      markActive(nextIndex, false);

      if (autoPlay) {
        const playPromise = st.audio.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        markActive(nextIndex, true);
      }
    }

    function next(autoPlay) {
      loadTrack(st.currentIndex + 1, !!autoPlay);
    }

    function prev(autoPlay) {
      loadTrack(st.currentIndex - 1, !!autoPlay);
    }

    function toggleShuffle() {
      st.shuffle = !st.shuffle;
      if (st.btnShuffle) st.btnShuffle.classList.toggle('is-active', st.shuffle);
    }

    // Bind controls
    if (st.btnNext) {
      st.onNext = () => next(true);
      st.btnNext.addEventListener('click', st.onNext);
    }

    if (st.btnPrev) {
      st.onPrev = () => prev(true);
      st.btnPrev.addEventListener('click', st.onPrev);
    }

    if (st.btnShuffle) {
      st.onShuffle = () => toggleShuffle();
      st.btnShuffle.addEventListener('click', st.onShuffle);
    }

    st.rows.forEach((row, idx) => {
      const fn = () => loadTrack(idx, true);
      st.onRowClick.push(fn);
      row.addEventListener('click', fn);
    });

    if (st.audio) {
      st.onEnded = () => {
        if (!st.rows.length) return;
        if (st.shuffle) {
          const r = Math.floor(Math.random() * st.rows.length);
          loadTrack(r, true);
        } else {
          next(true);
        }
      };
      st.audio.addEventListener('ended', st.onEnded);

      // ASL: pause all section-registered media via coordinator
      //      R-06: also pause cross-section YouTube player
      st.onPlay = () => {
        if (ROOT.aaMediaCoordinator) {
          ROOT.aaMediaCoordinator.pauseAllExcept({ type: 'audio', el: st.audio });
        }
        // R-06: audio started → pause YouTube player in sibling section
        if (window._uuYTPlayer && typeof window._uuYTPlayer.pauseVideo === 'function') {
          window._uuYTPlayer.pauseVideo();
        }
      };
      st.audio.addEventListener('play', st.onPlay);
    }

    // R-06: load YouTube IFrame API (guarded — script appended once per page)
    loadYTAPI();

    // Initial track load (no autoplay)
    if (st.rows.length) loadTrack(0, false);
  }

  function destroyRoot(root) {
    const st = stateByRoot.get(root);
    if (!st) return;

    if (st.btnNext    && st.onNext)    st.btnNext.removeEventListener('click', st.onNext);
    if (st.btnPrev    && st.onPrev)    st.btnPrev.removeEventListener('click', st.onPrev);
    if (st.btnShuffle && st.onShuffle) st.btnShuffle.removeEventListener('click', st.onShuffle);

    st.rows.forEach((row, idx) => {
      const fn = st.onRowClick[idx];
      if (fn) row.removeEventListener('click', fn);
    });

    if (st.audio) {
      if (st.onEnded) st.audio.removeEventListener('ended', st.onEnded);
      if (st.onPlay)  st.audio.removeEventListener('play',  st.onPlay);
    }

    // R-06: null out global YT player reference on section unload
    window._uuYTPlayer = null;

    if (ROOT.aaMediaCoordinator && st.sectionId) {
      ROOT.aaMediaCoordinator.unregisterSection(st.sectionId);
    }

    stateByRoot.delete(root);
  }

  function boot() {
    qsa(document, '[data-uu-release-player-root]').forEach(initRoot);
  }

  // Expose minimal API
  ROOT.aaReleasePlayer        = ROOT.aaReleasePlayer || {};
  ROOT.aaReleasePlayer.init   = (root) => initRoot(root);
  ROOT.aaReleasePlayer.destroy = (root) => destroyRoot(root);

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme Editor lifecycle
  document.addEventListener('shopify:section:load', (evt) => {
    const root = evt && evt.target ? evt.target.querySelector('[data-uu-release-player-root]') : null;
    if (root) initRoot(root);
  });

  document.addEventListener('shopify:section:unload', (evt) => {
    const root = evt && evt.target ? evt.target.querySelector('[data-uu-release-player-root]') : null;
    if (root) destroyRoot(root);
  });
})();
